// deps
import React, { memo, useCallback, useContext, useMemo } from "react";

// internal-deps
import { SlideButton, format, useEffectOnce } from "@swiggy-private/core-ui";
import { useSessionStorage } from "@swiggy-private/checkout-core";
import { throttle } from "@swiggy-private/js-utils";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styled-components
import {
    Container,
    PaymentMethodSlideButtonContainer,
    PrimaryContainer,
    DisplayNameContainer,
    AutoPaymentMethodContainer,
    Separator,
    TitleContainer,
} from "./styles";

// constants
import {
    BUMP_ANIMATION_TIMEOUT,
    SLIDE_BUMP_ANIMATION_SHOWN,
    SLIDE_TO_PAY_PREFIX,
    SLIDE_TO_PAY_AMOUNT,
    SLIDE_UPDATE_THROTTLE_TIME,
    TURBO_AUTO_PAY_CTA_TEXT,
} from "@payment-ui/constants/TurboPayment";
import { TurboPaymentContext } from "@payment-ui/components/turbo-payment-widget/TurboPaymentContext";
import { PLACE_ORDER_EXTRA_PARAMS } from "@payment-ui/constants";

// interfaces
import {
    PaymentGroupActions,
    PaymentMethodDowntime,
    TurboAutoPayMethod,
    TurboPaymentMethod,
} from "@payment-ui/interfaces";

// widgets/components
import { ChangePaymentCtaV2 } from "@payment-ui/widgets/change-payment-cta-v2";
import { ChangePaymentCta } from "@payment-ui/widgets/change-payment-cta";

// helpers
import { SliderButtonContext } from "@payment-ui/components/turbo-payment-widget/SliderButtonContext";

interface ITurboPaymentMethodProps {
    methodData: TurboAutoPayMethod;
    paymentActions: PaymentGroupActions<TurboPaymentMethod>;
    onClickChangePaymentCta: VoidFunction;
    amountWithDiscount: string;
    amountWithoutDiscount?: string;
    paymentMethodDowntime: PaymentMethodDowntime;
    showDiscountedAmount: boolean;
    onLoad: VoidFunction;
}

function TurboPaymentDownTimeWidget(props: ITurboPaymentMethodProps) {
    const {
        methodData,
        paymentMethodDowntime,
        amountWithDiscount,
        amountWithoutDiscount,
        paymentActions,
        onClickChangePaymentCta,
        showDiscountedAmount,
        onLoad,
    } = props;

    const [isBumpAnimationShown, setBumpAnimationShown] = useSessionStorage(SLIDE_BUMP_ANIMATION_SHOWN, false);
    const { onSlideButtonDrag, onSlideButtonSuccess, showChangeAnimation } = useContext(TurboPaymentContext);
    const { sliderButtonRef } = useContext(SliderButtonContext);

    useEffectOnce(() => {
        const timeout = setTimeout(() => {
            setBumpAnimationShown(true);
        }, BUMP_ANIMATION_TIMEOUT);

        onLoad();

        return () => {
            clearTimeout(timeout);
        };
    });

    const onPay = useCallback(
        (extraData = {}): void => {
            // PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT is made undefined as swiggy money is not there in
            // for turbopayment currently
            paymentActions.onPay(
                methodData,
                { [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: undefined },
                {
                    isTurboPaymentMethod: true,
                    isPaymentMethodFacingDowntime: true,
                    ...extraData,
                },
            );
        },
        [methodData, paymentActions],
    );

    const onPayButtonSlide = useCallback(
        (sliderPosition, containerWidth) => {
            onSlideButtonSuccess?.(sliderPosition, containerWidth);

            onPay({ slidePercent: (sliderPosition / containerWidth) * 100 });
        },
        [onPay, onSlideButtonSuccess],
    );

    const renderTurboChangeButton = useMemo(() => {
        if (showChangeAnimation) {
            return (
                <ChangePaymentCtaV2
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                    onClick={onClickChangePaymentCta}
                />
            );
        } else {
            return (
                <ChangePaymentCta
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                    onClick={onClickChangePaymentCta}
                />
            );
        }
    }, [onClickChangePaymentCta, showChangeAnimation]);

    const turboCtaText = useMemo(() => {
        const amount = showDiscountedAmount ? amountWithDiscount : amountWithoutDiscount;

        return format(TURBO_AUTO_PAY_CTA_TEXT, amount || "");
    }, [amountWithDiscount, amountWithoutDiscount, showDiscountedAmount]);

    return (
        <Container>
            <AutoPaymentMethodContainer>
                <PrimaryContainer>
                    <DisplayNameContainer>
                        <div>
                            <TitleContainer color="text_Low_Emphasis" variant="V2.overline.neutral.bold">
                                {paymentMethodDowntime?.singleClickPresentationInfo?.title || ""}
                            </TitleContainer>
                            <Separator />
                        </div>
                        {renderTurboChangeButton}
                    </DisplayNameContainer>
                    <Text color="text_Highest_Emphasis" variant="V2.subheading1.neutral.bold">
                        {paymentMethodDowntime?.singleClickPresentationInfo?.subtitle || ""}
                    </Text>
                </PrimaryContainer>
            </AutoPaymentMethodContainer>
            <PaymentMethodSlideButtonContainer>
                <SlideButton
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.SLIDER_BUTTON}
                    sliderButtonRef={sliderButtonRef}
                    showInitialBumpAnimation={!isBumpAnimationShown}
                    onDragUpdate={throttle(onSlideButtonDrag, SLIDE_UPDATE_THROTTLE_TIME)}
                    onSuccess={onPayButtonSlide}
                    turboCtaText={turboCtaText}
                    prefixText={SLIDE_TO_PAY_PREFIX}
                    amountText={format(SLIDE_TO_PAY_AMOUNT, amountWithDiscount || "")}
                />
            </PaymentMethodSlideButtonContainer>
        </Container>
    );
}
export default memo(TurboPaymentDownTimeWidget);
